/**
 * Whitelabel Visibility Hook
 *
 * Provides centralized visibility logic for UI elements based on:
 * - Whitelabel mode (enabled/disabled)
 * - User role (SUPER_ADMIN vs regular users)
 *
 * In whitelabel mode, these elements are always hidden (for all users):
 * - Browse Hunts links
 * - SaaS footer links
 * - Create Hunt button in navigation (handled separately in Navigation.tsx)
 *
 * In whitelabel mode, non-admin users have additional restrictions:
 * - Create Hunt button (in dashboard)
 * - Subscription/upgrade options
 * - My Campaigns tab
 * - Dashboard admin items
 * - Settings admin tabs
 *
 * SUPER_ADMIN users see admin features (campaigns, settings) but not SaaS-specific UI.
 *
 * @module useWhitelabelVisibility
 */

import { useMemo } from 'react';
import { isWhitelabelMode } from '@/lib/whitelabel-config';

/**
 * User role type matching the Role enum in the Prisma schema
 */
export type UserRole = 'USER' | 'SUPER_ADMIN';

/**
 * Visibility flags returned by the hook
 */
export interface WhitelabelVisibility {
  /** Whether whitelabel mode is enabled */
  isWhitelabelMode: boolean;

  /** Show "Create Hunt" button (false for non-admin in whitelabel; nav also checks isWhitelabelMode) */
  showCreateHunt: boolean;

  /** Show subscription/upgrade options (false in whitelabel for non-admins) */
  showSubscription: boolean;

  /** Show "My Campaigns" tab in navigation (false for non-admin in whitelabel) */
  showMyCampaigns: boolean;

  /** Show dashboard admin items: Create Campaign, IYK Chips, Integrations (false for non-admin in whitelabel) */
  showDashboardAdmin: boolean;

  /** Show settings admin tabs: Organization, Subscription, Integrations (false for non-admin in whitelabel) */
  showSettingsAdmin: boolean;

  /** Show SaaS footer links: Status, API Docs, Changelog, Pricing, Help Center, Security (false in whitelabel) */
  showSaasFooterLinks: boolean;

  /** Show "Browse Hunts" link in navigation (false in whitelabel mode - users only see their hunts) */
  showBrowseHunts: boolean;

  /** Show "How it Works" link in navigation (false in whitelabel mode) */
  showHowItWorks: boolean;
}

/**
 * Get visibility flags based on whitelabel mode and user role.
 * This is a pure function that can be used without React hooks.
 *
 * @param role - The user's role (USER or SUPER_ADMIN)
 * @returns Visibility flags for UI elements
 *
 * @example
 * ```typescript
 * const visibility = getWhitelabelVisibility('USER');
 * if (visibility.showCreateHunt) {
 *   // Render Create Hunt button
 * }
 * ```
 */
export function getWhitelabelVisibility(role?: UserRole | string | null): WhitelabelVisibility {
  const whitelabelEnabled = isWhitelabelMode();
  const isSuperAdmin = role === 'SUPER_ADMIN';

  // In SaaS mode (whitelabel disabled), everyone sees all features
  if (!whitelabelEnabled) {
    return {
      isWhitelabelMode: false,
      showCreateHunt: true,
      showSubscription: true,
      showMyCampaigns: true,
      showDashboardAdmin: true,
      showSettingsAdmin: true,
      showSaasFooterLinks: true,
      showBrowseHunts: true,
      showHowItWorks: true,
    };
  }

  // In whitelabel mode, SUPER_ADMIN sees admin features
  // But SaaS footer links and Browse Hunts are always hidden in whitelabel mode for brand consistency
  if (isSuperAdmin) {
    return {
      isWhitelabelMode: true,
      showCreateHunt: true,
      showSubscription: true,
      showMyCampaigns: true,
      showDashboardAdmin: true,
      showSettingsAdmin: true,
      showSaasFooterLinks: false, // Always hide SaaS links in whitelabel for brand consistency
      showBrowseHunts: false, // Admins access campaigns via dashboard
      showHowItWorks: false, // Hide SaaS-specific content in whitelabel
    };
  }

  // In whitelabel mode, non-admin users have restricted visibility
  // They only see landing page, tenant page, and hunt/clue pages
  return {
    isWhitelabelMode: true,
    showCreateHunt: false,
    showSubscription: false,
    showMyCampaigns: false,
    showDashboardAdmin: false,
    showSettingsAdmin: false,
    showSaasFooterLinks: false,
    showBrowseHunts: false, // Users only see hunts they're participating in
    showHowItWorks: false, // Hide SaaS-specific content in whitelabel
  };
}

/**
 * React hook for getting whitelabel visibility flags.
 * Memoizes the result based on the user role.
 *
 * @param role - The user's role from session (e.g., session.user.tenants[0].role)
 * @returns Visibility flags for UI elements
 *
 * @example
 * ```typescript
 * function Navigation() {
 *   const { data: session } = useSession();
 *   const role = session?.user?.tenants?.[0]?.role;
 *   const { showCreateHunt, showSubscription } = useWhitelabelVisibility(role);
 *
 *   return (
 *     <nav>
 *       {showCreateHunt && <CreateHuntButton />}
 *       {showSubscription && <UpgradeLink />}
 *     </nav>
 *   );
 * }
 * ```
 */
export function useWhitelabelVisibility(role?: UserRole | string | null): WhitelabelVisibility {
  return useMemo(() => getWhitelabelVisibility(role), [role]);
}
